import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { get } from '../utils/api';

const initialProfile = {
  subject: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  billing: {
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
  },
  meta: {
    spouse: '',
    family_members: [],
    gothram: '',
  },
};

export function mapCustomerToProfile(data) {
  if (!data) return { ...initialProfile };
  const fm = data.meta_data?.billing_family_members || '';
  const family_members = Array.isArray(fm)
    ? fm
    : String(fm || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  return {
    subject: data.subject || '',
    first_name: data.first_name || data.billing_first_name || '',
    last_name: data.last_name || data.billing_last_name || '',
    email: data.email || data.billing_email || '',
    phone: data.phone || data.billing_phone || '',
    billing: {
      address_1: data.billing_address_1 || '',
      address_2: data.billing_address_2 || '',
      city: data.billing_city || '',
      state: data.billing_state || '',
      postcode: data.billing_postcode || '',
      country: data.billing_country || 'US',
    },
    meta: {
      spouse: data.meta_data?.billing_spouse || '',
      gothram: data.meta_data?.billing_gothram || '',
      family_members,
    },
  };
}

const PortalProfileContext = createContext(null);

export function PortalProfileProvider({ children }) {
  const { id } = useParams();
  const [profile, setProfile] = useState(initialProfile);
  const [detailsLoading, setDetailsLoading] = useState(true);

  const reloadProfile = useCallback(async () => {
    const data = await get('/portal/me/customer');
    setProfile(mapCustomerToProfile(data));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setDetailsLoading(true);
        const data = await get('/portal/me/customer');
        if (!cancelled) setProfile(mapCustomerToProfile(data));
      } catch (e) {
        console.warn('[PortalProfile] load failed', e?.message || e);
        if (!cancelled) setProfile({ ...initialProfile });
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const value = useMemo(
    () => ({ profile, setProfile, detailsLoading, reloadProfile }),
    [profile, detailsLoading, reloadProfile]
  );

  return (
    <PortalProfileContext.Provider value={value}>
      {children}
    </PortalProfileContext.Provider>
  );
}

export function usePortalProfile() {
  const ctx = useContext(PortalProfileContext);
  if (!ctx) throw new Error('usePortalProfile must be used within PortalProfileProvider');
  return ctx;
}
